import {
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue
} from '@databases/types/database';
import { type ValueChange } from '@databases/utils/database/changesets';
import { parseDate } from '@databases/utils/database/field-types/date';

import { type TimelineItemColors } from './timeline-colors';
import {
  TIMELINE_DAY_WIDTH,
  TIMELINE_MONTH_BUFFER_MONTHS,
  TIMELINE_MONTH_WIDTH,
  TIMELINE_QUARTER_MONTH_WIDTH,
  TIMELINE_WEEK_DAY_WIDTH
} from './timeline-constants';
import { type TimelineScale } from './view-layout-module';

export type TimelineDateField = DatabaseField<DatabaseFieldType.DATE>;

export type TimelineFields = {
  startDateField?: TimelineDateField;
  endDateField?: TimelineDateField;
};

export type TimelineItem = {
  colors?: TimelineItemColors;
  entryId: string;
  hasEndDateValue?: boolean;
  hasStartDateValue?: boolean;
  isRangeInvalid?: boolean;
  title: string;
  startDate?: Date;
  endDate?: Date;
};

export type TimelineColumn = {
  date: Date;
  isWeekend: boolean;
  key: string;
  label: string;
};

export type TimelineHeader = {
  key: string;
  label: string;
  span: number;
};

export type Timeline = {
  columns: TimelineColumn[];
  headers: TimelineHeader[];
  startDate?: Date;
};

export type TimelinePositioning = {
  columnOffsets: readonly number[];
  columnWidths: readonly number[];
  scale: TimelineScale;
  width: number;
};

export type TimelineOffsetBoundary = 'start' | 'end';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

export const isTimelineDateField = (field: DatabaseField): field is TimelineDateField =>
  field.type === DatabaseFieldType.DATE;

export const getDefaultTimelineFields = (fields: DatabaseField[]): TimelineFields => {
  const dateFields = fields.filter(isTimelineDateField);

  return {
    startDateField: dateFields[0],
    endDateField: dateFields[1]
  };
};

export const parseTimelineDate = (value: string | undefined): Date | undefined => {
  const parsedValue = parseDate(value);

  if (!parsedValue) {
    return undefined;
  }

  const [datePart] = parsedValue.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) {
    return undefined;
  }

  return new Date(Date.UTC(year, month - 1, day));
};

export const getDayDifference = (start: Date, end: Date) =>
  Math.round((end.getTime() - start.getTime()) / DAY_IN_MS);

export const addDays = (date: Date, days: number) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days));

const getMonthDifference = (start: Date, end: Date) =>
  (end.getUTCFullYear() - start.getUTCFullYear()) * 12 +
  end.getUTCMonth() -
  start.getUTCMonth();

const getDaysInMonth = (date: Date) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();

const getMondaysInMonth = (date: Date) => {
  const monthStart = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  const firstMondayOffset = (8 - monthStart.getUTCDay()) % 7;

  return 1 + Math.floor((getDaysInMonth(date) - firstMondayOffset - 1) / 7);
};

export const formatTimelineDate = (date: Date) =>
  [
    date.getUTCFullYear(),
    `${date.getUTCMonth() + 1}`.padStart(2, '0'),
    `${date.getUTCDate()}`.padStart(2, '0')
  ].join('-');

const getTimelineColumnIndex = (
  positioning: TimelinePositioning,
  offset: number,
  boundary: TimelineOffsetBoundary = 'start'
) => {
  if (positioning.columnWidths.length === 0) {
    return 0;
  }

  const clampedOffset =
    boundary === 'end'
      ? Math.min(Math.max(0, offset), positioning.width)
      : Math.min(Math.max(0, offset), Math.max(0, positioning.width - Number.EPSILON));
  let startIndex = 0;
  let endIndex = positioning.columnWidths.length - 1;

  while (startIndex <= endIndex) {
    const columnIndex = Math.floor((startIndex + endIndex) / 2);
    const columnStart = positioning.columnOffsets[columnIndex] ?? 0;
    const columnEnd = columnStart + (positioning.columnWidths[columnIndex] ?? 0);
    const boundaryTolerance = Math.max(1, Math.abs(clampedOffset)) * Number.EPSILON * 4;

    if (
      boundary === 'end' &&
      columnIndex > 0 &&
      Math.abs(clampedOffset - columnStart) <= boundaryTolerance
    ) {
      return columnIndex - 1;
    }

    if (clampedOffset < columnStart) {
      endIndex = columnIndex - 1;
    } else if (
      clampedOffset > columnEnd ||
      (boundary === 'start' && clampedOffset === columnEnd)
    ) {
      startIndex = columnIndex + 1;
    } else {
      return columnIndex;
    }
  }

  return positioning.columnWidths.length - 1;
};

export const getTimelinePositioning = (
  columns: TimelineColumn[],
  scale: TimelineScale
): TimelinePositioning => {
  const columnWidths = columns.map((column) => {
    switch (scale) {
      case 'weeks':
        return TIMELINE_WEEK_DAY_WIDTH;
      case 'months':
        return (
          (TIMELINE_MONTH_WIDTH * getMondaysInMonth(column.date)) /
          (5 * getDaysInMonth(column.date))
        );
      case 'quarters':
        return TIMELINE_QUARTER_MONTH_WIDTH;
      case 'days':
      case 'years':
        return TIMELINE_DAY_WIDTH;
    }
  });
  let width = 0;
  const columnOffsets = columnWidths.map((columnWidth) => {
    const columnOffset = width;
    width += columnWidth;
    return columnOffset;
  });

  return {
    columnOffsets,
    columnWidths,
    scale,
    width
  };
};

export const getDateFromTimelineOffset = (
  timelineStartDate: Date,
  offset: number,
  positioning: TimelinePositioning,
  boundary: TimelineOffsetBoundary = 'start'
) => {
  const columnIndex = getTimelineColumnIndex(positioning, offset, boundary);

  if (positioning.scale !== 'quarters') {
    return addDays(timelineStartDate, columnIndex);
  }

  const month = new Date(
    Date.UTC(
      timelineStartDate.getUTCFullYear(),
      timelineStartDate.getUTCMonth() + columnIndex,
      1
    )
  );
  const columnWidth = positioning.columnWidths[columnIndex] ?? TIMELINE_DAY_WIDTH;
  const offsetInMonth = Math.max(
    0,
    offset - (positioning.columnOffsets[columnIndex] ?? 0)
  );
  const daysInMonth = getDaysInMonth(month);
  const dayWidth = columnWidth / daysInMonth;
  const dayIndex = Math.min(
    daysInMonth - 1,
    Math.max(
      0,
      boundary === 'end'
        ? Math.ceil(offsetInMonth / dayWidth) - 1
        : Math.floor(offsetInMonth / dayWidth)
    )
  );

  return addDays(month, dayIndex);
};

export const getDateOffset = (
  timelineStartDate: Date,
  date: Date,
  positioning: TimelinePositioning,
  includeFullDay = false
) => {
  if (positioning.scale !== 'quarters') {
    const dayIndex = getDayDifference(timelineStartDate, date);
    const firstColumnWidth = positioning.columnWidths[0] ?? 0;
    const lastColumnWidth =
      positioning.columnWidths[positioning.columnWidths.length - 1] ?? 0;

    if (dayIndex < 0) {
      return (dayIndex + (includeFullDay ? 1 : 0)) * firstColumnWidth;
    }

    if (dayIndex >= positioning.columnWidths.length) {
      return (
        positioning.width +
        (dayIndex - positioning.columnWidths.length + (includeFullDay ? 1 : 0)) *
          lastColumnWidth
      );
    }

    const columnOffset = positioning.columnOffsets[dayIndex] ?? 0;
    const columnWidth = positioning.columnWidths[dayIndex] ?? 0;

    return includeFullDay ? columnOffset + columnWidth : columnOffset;
  }

  const monthIndex = getMonthDifference(timelineStartDate, date);
  const firstColumnWidth = positioning.columnWidths[0] ?? 0;
  const lastColumnWidth =
    positioning.columnWidths[positioning.columnWidths.length - 1] ?? 0;

  if (monthIndex < 0) {
    return monthIndex * firstColumnWidth;
  }

  if (monthIndex >= positioning.columnWidths.length) {
    return (
      positioning.width + (monthIndex - positioning.columnWidths.length) * lastColumnWidth
    );
  }

  const monthOffset = positioning.columnOffsets[monthIndex] ?? 0;
  const monthWidth = positioning.columnWidths[monthIndex] ?? 0;
  const dayOffset =
    ((date.getUTCDate() - (includeFullDay ? 0 : 1)) / getDaysInMonth(date)) * monthWidth;

  return monthOffset + dayOffset;
};

export const getTimelineDateColumnIndex = (
  timelineStartDate: Date,
  date: Date,
  positioning: TimelinePositioning
) =>
  getTimelineColumnIndex(
    positioning,
    getDateOffset(timelineStartDate, date, positioning)
  );

export const createTimelineDateValue = (date: Date) => {
  const value = formatTimelineDate(date);

  return {
    value: {
      type: DatabaseFieldType.DATE,
      value
    } as DatabaseValue<DatabaseFieldType.DATE>,
    change: {
      type: DatabaseFieldType.DATE,
      value
    } as ValueChange
  };
};

const getMonthLabel = (date: Date, locale: string) =>
  new Intl.DateTimeFormat(locale, {
    month: 'long',
    timeZone: 'UTC'
  }).format(date);

const getShortMonthLabel = (date: Date, locale: string) =>
  new Intl.DateTimeFormat(locale, {
    month: 'short',
    timeZone: 'UTC'
  })
    .format(date)
    .slice(0, 3);

const getMonthHeaderLabel = (date: Date, locale: string) =>
  `${getMonthLabel(date, locale)} ${date.getUTCFullYear()}`;

const groupColumnsByMonth = (
  columns: TimelineColumn[],
  locale: string
): TimelineHeader[] =>
  columns.reduce<TimelineHeader[]>((headers, column) => {
    const key = `${column.date.getUTCFullYear()}-${column.date.getUTCMonth()}`;
    const previous = headers[headers.length - 1];

    if (previous?.key === key) {
      previous.span += 1;
      return headers;
    }

    headers.push({
      key,
      label: getMonthHeaderLabel(column.date, locale),
      span: 1
    });
    return headers;
  }, []);

const getDayLabel = (date: Date, locale: string) =>
  new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    timeZone: 'UTC'
  }).format(date);

const getIsoWeek = (date: Date) => {
  const target = new Date(date);
  const dayNumber = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNumber);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const weekNumber = Math.ceil(
    ((target.getTime() - yearStart.getTime()) / DAY_IN_MS + 1) / 7
  );

  return {
    weekNumber,
    year: target.getUTCFullYear()
  };
};

const isWeekendDate = (date: Date) => {
  const day = date.getUTCDay();

  return day === 0 || day === 6;
};

const getScheduledDateRange = (items: TimelineItem[]) => {
  const scheduledDates = items.flatMap((item) =>
    [item.startDate, item.endDate].filter((date): date is Date => Boolean(date))
  );
  const fallback = new Date();
  const fallbackDate = new Date(
    Date.UTC(fallback.getFullYear(), fallback.getMonth(), fallback.getDate())
  );

  if (!scheduledDates.length) {
    return {
      minDate: fallbackDate,
      maxDate: fallbackDate
    };
  }

  const scheduledRange = scheduledDates.reduce(
    (range, date) => ({
      minTime: Math.min(range.minTime, date.getTime()),
      maxTime: Math.max(range.maxTime, date.getTime())
    }),
    {
      minTime: Number.POSITIVE_INFINITY,
      maxTime: Number.NEGATIVE_INFINITY
    }
  );

  return {
    minDate: new Date(scheduledRange.minTime),
    maxDate: new Date(scheduledRange.maxTime)
  };
};

const getBufferedScheduledDateRange = (items: TimelineItem[]) => {
  const { minDate, maxDate } = getScheduledDateRange(items);

  return {
    firstDate: new Date(
      Date.UTC(
        minDate.getUTCFullYear(),
        minDate.getUTCMonth() - TIMELINE_MONTH_BUFFER_MONTHS,
        1
      )
    ),
    lastDate: new Date(
      Date.UTC(
        maxDate.getUTCFullYear(),
        maxDate.getUTCMonth() + TIMELINE_MONTH_BUFFER_MONTHS + 1,
        0
      )
    )
  };
};

const groupColumnsByWeek = (
  columns: TimelineColumn[],
  locale: string
): TimelineHeader[] => {
  const headers = columns.reduce<TimelineHeader[]>((result, column) => {
    const week = getIsoWeek(column.date);
    const key = `${week.year}-${week.weekNumber}`;
    const previous = result[result.length - 1];

    if (previous?.key === key) {
      previous.span += 1;
      return result;
    }

    result.push({
      key,
      label: '',
      span: 1
    });
    return result;
  }, []);

  let columnOffset = 0;
  const displayedMonths = new Set<string>();

  return headers.map((header) => {
    const headerColumns = columns.slice(columnOffset, columnOffset + header.span);
    const monthLabels = [
      ...new Set(headerColumns.map((column) => getMonthHeaderLabel(column.date, locale)))
    ].filter((label) => !displayedMonths.has(label));
    monthLabels.forEach((label) => displayedMonths.add(label));
    columnOffset += header.span;

    return {
      ...header,
      label: monthLabels.join(' / ')
    };
  });
};

const getQuarterStartMonth = (month: number) => month - (month % 3);

const getQuarterLabel = (date: Date) => {
  const quarter = Math.floor(date.getUTCMonth() / 3) + 1;

  return `Q${quarter} ${date.getUTCFullYear()}`;
};

const groupColumnsByQuarter = (columns: TimelineColumn[]): TimelineHeader[] =>
  columns.reduce<TimelineHeader[]>((headers, column) => {
    const quarterStartMonth = getQuarterStartMonth(column.date.getUTCMonth());
    const key = `${column.date.getUTCFullYear()}-${quarterStartMonth}`;
    const previous = headers[headers.length - 1];

    if (previous?.key === key) {
      previous.span += 1;
      return headers;
    }

    headers.push({
      key,
      label: getQuarterLabel(column.date),
      span: 1
    });
    return headers;
  }, []);

export const getMonthTimeline = (items: TimelineItem[], locale: string): Timeline => {
  const { firstDate, lastDate } = getBufferedScheduledDateRange(items);
  const columns: TimelineColumn[] = [];
  const current = new Date(firstDate);

  while (current <= lastDate) {
    const date = new Date(current);
    columns.push({
      date,
      isWeekend: isWeekendDate(date),
      key: date.toISOString(),
      label: getDayLabel(date, locale)
    });
    current.setUTCDate(current.getUTCDate() + 1);
  }

  return {
    columns,
    headers: groupColumnsByMonth(columns, locale),
    startDate: columns[0]?.date
  };
};

export const getQuarterTimeline = (items: TimelineItem[], locale: string): Timeline => {
  const { firstDate, lastDate } = getBufferedScheduledDateRange(items);
  const columns: TimelineColumn[] = [];
  const firstQuarterStartMonth = getQuarterStartMonth(firstDate.getUTCMonth());
  const lastQuarterStartMonth = getQuarterStartMonth(lastDate.getUTCMonth());
  const current = new Date(
    Date.UTC(firstDate.getUTCFullYear(), firstQuarterStartMonth, 1)
  );
  const last = new Date(
    Date.UTC(lastDate.getUTCFullYear(), lastQuarterStartMonth + 2, 1)
  );

  while (current <= last) {
    const date = new Date(current);
    columns.push({
      date,
      isWeekend: false,
      key: date.toISOString(),
      label: getShortMonthLabel(date, locale)
    });
    current.setUTCMonth(current.getUTCMonth() + 1);
  }

  return {
    columns,
    headers: groupColumnsByQuarter(columns),
    startDate: columns[0]?.date
  };
};

export const getWeekTimeline = (items: TimelineItem[], locale: string): Timeline => {
  const { firstDate: bufferedFirstDate, lastDate: bufferedLastDate } =
    getBufferedScheduledDateRange(items);
  const columns: TimelineColumn[] = [];
  const firstDate = addDays(
    bufferedFirstDate,
    -((bufferedFirstDate.getUTCDay() + 6) % 7)
  );
  const lastDate = addDays(
    bufferedLastDate,
    6 - ((bufferedLastDate.getUTCDay() + 6) % 7)
  );
  const current = new Date(firstDate);

  while (current <= lastDate) {
    const date = new Date(current);
    columns.push({
      date,
      isWeekend: isWeekendDate(date),
      key: date.toISOString(),
      label: getDayLabel(date, locale)
    });
    current.setUTCDate(current.getUTCDate() + 1);
  }

  return {
    columns,
    headers: groupColumnsByWeek(columns, locale),
    startDate: columns[0]?.date
  };
};

export const getTimeline = (
  items: TimelineItem[],
  scale: TimelineScale,
  locale: string
): Timeline => {
  if (scale === 'weeks') {
    return getWeekTimeline(items, locale);
  }

  if (scale === 'quarters') {
    return getQuarterTimeline(items, locale);
  }

  return getMonthTimeline(items, locale);
};
